import * as React from "react";
import {autorun, runInAction} from "mobx";
import {observer} from "mobx-react";

import "./basemap-gallery.scss";

import EsriBasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import EsriBasemapGalleryViewModel from "@arcgis/core/widgets/BasemapGallery/BasemapGalleryViewModel";

import {BasemapGalleryConfiguration} from "../../config/basemap-gallery-configuration";
import {BasemapGalleryItemData} from "./basemap-gallery-item/basemap-gallery-item.data";
import {BasemapGalleryItem} from "./basemap-gallery-item/basemap-gallery-item";
import {BasemapGalleryButton} from "./basemap-gallery-button/basemap-gallery-button";
import {PopupBase} from "../popup/popup";
import {Icon} from "../icon/icon";
import {Input, TransitionablePortal} from "semantic-ui-react";
import { Slider } from "react-semantic-ui-range";
import {FormatUtils} from "../../../utils/format-utils";
import {BasemapGalleryToggle} from "./basemap-gallery-toggle/basemap-gallery-toggle";
import {IAppStore, withStore} from "../../stores/abstract-app-store";

export interface BasemapGalleryProps {
    config: BasemapGalleryConfiguration;  // widgetsSearch is also the type for configuration object
    onLoad?: (basemapGalleryViewModel: EsriBasemapGalleryViewModel) => any;
    proxyUrl?: string;
    small?: boolean;
    store?: IAppStore; // this is injected by withStore
}

@observer
class BasemapGalleryBase extends React.Component<BasemapGalleryProps, any> {
    autorunDisposers: any[] = [];
    watchHandles: any[] = [];
    mapToggle: string[] = [];
    widgetKey = "basemap-gallery";
    constructor(props: BasemapGalleryProps) {
        super(props);

        this.state = {
            animation: "slide up",
            duration: 200,
            filterValue: "",
            isLoading: true,
            isExpanded: (typeof this.props.small === "boolean" && this.props.small),
            viewModel: null,
            dataAll: null, // gallery data items
            dataShown: null,
            activeBasemapId: null,
            orthophotoTag: "Orthophoto",
            isOrthophoto: null,
            timer: null,
            transparency: 0,
            showSlider: false,
            sliderWorkaroundWait: 200,
            visible: true
        };
        if (typeof this.props.small === "boolean" && this.props.small) {
            this.widgetKey += "-small";
        }
        this.handleItemClick = this.handleItemClick.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.clearFilterInputField = this.clearFilterInputField.bind(this);
        this.handleExpandButtonClick = this.handleExpandButtonClick.bind(this);
        this.handleToggleButtonClick = this.handleToggleButtonClick.bind(this);
        this.setTransparency = this.setTransparency.bind(this);
        this.close = this.close.bind(this);
    }

    showSlider(): void {
        this.setState({showSlider : true});
    }

    hideSlider(): void {
        this.setState({showSlider : false});
    }

    render(): JSX.Element | null {
        const {config, small} = this.props;
        const {
            viewModel,
            animation,
            duration,
            dataAll,
            dataShown,
            filterValue,
            isExpanded,
            isLoading,
            activeBasemapId,
            isOrthophoto,
            showSlider,
            transparency,
            visible
        } = this.state;

        if (isLoading) {
            return (
                <BasemapGalleryButton
                    isExpanded={isExpanded}
                    isLoading={isLoading}
                    isOrthophoto={!isOrthophoto}
                    mapToggle={this.mapToggle}
                    duration={duration}
                    onClick={this.handleExpandButtonClick}
                />
            );
        }

        if(viewModel){
            this.setLayerOpacity(viewModel.activeBasemap.baseLayers);
        }
        if (!visible) {
            return null;
        }
        { /* Basemap Gallery*/
        }
        let menuGallery = <div />;
        if (isExpanded) {
            // workaround to show correct start position on slider
            // see https://github.com/iozbeyli/react-semantic-ui-range/issues/13
            if (!showSlider) {
                setTimeout(() => {
                    this.showSlider();
                }, this.state.sliderWorkaroundWait);
            }

            const sliderSettings = {
                min: 0,
                max: 100,
                step: 1,
                onChange: (value: number) => {
                    this.setTransparency(value);
                }
            };
            let addClass = "geo-component  geo-basemap-gallery";
            if (!small) {
                addClass += " geo-widget geo-popup--shadow";
            }
            menuGallery = <div
                className={addClass}>
                <div className="basemap-gallery-inner">
                    {(!small)?(
                        <div className="basemap-gallery-header">
                            {config.searchEnabled ?
                                <div className={"basemap-gallery-header-filter "
                                    + ((filterValue!=="")? "geo-input-has-value": "geo-input-has-no-value")}>
                                    <Input
                                        fluid
                                        size="small"
                                        placeholder="Suchbegriff..."
                                        value={filterValue}
                                        onChange={(event) => this.handleFilterChange(event.target.value)}/>
                                    <Icon name={"magnifying_glass"}/>
                                    <Icon
                                        className={"geo-clear-field"}
                                        name={"navigate_cross"}
                                        onClick={this.clearFilterInputField}
                                        title={"Text löschen"} />
                                </div>
                                :
                                <h4>Hintergrund</h4>}
                            <Icon
                                className="geo-close"
                                name="navigate_cross"
                                title={"schliessen"}
                                onClick={this.handleExpandButtonClick}/>
                        </div>): null}
                    <div className="basemap-gallery-tools">
                        <div className="basemap-gallery-slider-label">Transparenz / Helligkeit: {transparency}%</div>
                        {showSlider &&
                            <Slider
                                className="basemap-gallery-slider" color="blue"
                                value={transparency} settings={sliderSettings} />
                        }
                    </div>
                    <div className="basemap-gallery-detail">
                        <ul>
                            {dataShown.map((data: BasemapGalleryItemData) =>
                                <BasemapGalleryItem
                                    key={data.id}
                                    data={data}
                                    isSelected={data.id === activeBasemapId}
                                    onClick={() => this.handleItemClick(data)}
                                />
                            )}
                        </ul>
                    </div>
                </div>
            </div>;
        }
        const btnExpand = (
            <BasemapGalleryButton
                isExpanded={isExpanded}
                isLoading={isLoading}
                isOrthophoto={!isOrthophoto}
                mapToggle={this.mapToggle}
                duration={duration}
                onClick={this.handleExpandButtonClick}
            />
        );
        if (small) {
            return (<>{menuGallery}</>);
        } else {
            if(dataAll.length > 2) {
                return (
                    <TransitionablePortal
                        open={isExpanded}
                        trigger={btnExpand}
                        transition={{animation, duration}}
                        unmountOnHide={false}
                        onClose={this.close}>
                        {menuGallery}
                    </TransitionablePortal>
                );
            } else if(dataAll.length === 2) {
                return (
                    <BasemapGalleryToggle
                        activeBasemapId={activeBasemapId}
                        isLoading={isLoading}
                        mapToggle={dataAll}
                        onClick={this.handleToggleButtonClick}
                    />
                );
            }
            return null;
        }
    }

    open(): void {
        const {store} = this.props;
        this.setState({isExpanded: true});
        runInAction(()=> {
            const popup = store?.uiStore.widgets.get("popup") as PopupBase;
            if (popup) {
                popup.close(store);
            }
        });
    }

    close(): void {
        this.setState({
            isExpanded: false,
            showSlider: false
        });
    }
    handleToggleButtonClick(event: React.MouseEvent): void {
        const {store} = this.props;
        const {activeBasemapId, dataAll} = this.state;
        event.preventDefault();
        if (this.state.isLoading) {
            return;
        }
        const current = dataAll.find((b: BasemapGalleryItemData) => b.id !== activeBasemapId);
        const nextBasemap = this.getBasemap(current.id);

        this.setActiveBasemap(nextBasemap);
        store?.mapStore.setBasemapById({id: current.id, title: current.title, tags: current.tags});
    }
    handleExpandButtonClick(event: React.MouseEvent): void {
        event.preventDefault();

        if (this.state.isLoading) {
            return;
        }

        if (this.state.isExpanded) {
            this.resetFilter();
            this.close();
        } else {
            this.open();
        }
    }

    handleFilterChange(filterText: string): void {

        // reset list if no filter is defined
        if (!filterText) {
            this.resetFilter();
            return;
        }
        this.setState({filterValue: filterText});
        // defer applying filter to list
        this.handleDeferred(this.applyFilter, [this.state.dataAll, filterText], 100);
    }
    clearFilterInputField(): void {
        this.resetFilter();
    }

    handleDeferred(target: any, args: any[], timeout: number): void {

        // reset timer
        if (this.state.timer) {
            clearTimeout(this.state.timer);
        }

        // bind deferred method
        target = target.bind(this);

        // set up timer
        this.setState({
            timer: setTimeout(() => {
                target(...args);
            }, timeout)
        });
    }

    applyFilter(items: BasemapGalleryItemData[], filterText: string): void {

        const filterNormalized = filterText.trim().toLowerCase();

        const filteredItems = items.filter((item: BasemapGalleryItemData) => {

            const itemTextNormalized = item.title.trim().toLowerCase();

            return itemTextNormalized.search(filterNormalized) >= 0;
        });

        // add a placeholder if no result was found
        const itemsToShow = filteredItems.length ? filteredItems : [{
            id: 0,
            title: "Keine Grundkarten gefunden",
            isPlaceholder: true
        }];

        this.setState({dataShown: itemsToShow});
    }

    resetFilter(): void {
        this.setState((state: any) => ({
            dataShown: state.dataAll,
            filterValue: ""
        }));
    }

    handleItemClick(item: BasemapGalleryItemData): void {
        const {small, store} = this.props;
        // ignore click on placeholder item
        if (item.isPlaceholder) {
            return;
        }

        const selectedBasemap = this.getBasemap(item.id);

        this.setActiveBasemap(selectedBasemap);
        store?.mapStore.setBasemapById({id: item.id, title: item.title, tags: item.tags});
        if (small) {
            store?.uiStore.setSidebarVisible(false);
        }else {
            this.close();
        }
    }

    getBasemap(basemapId: string): any {
        if (!this.state.viewModel) {
            return null;
        }
        return this.state.viewModel.source.basemaps.find((basemap: any) => basemap.portalItem.id === basemapId) || null;
    }

    setActiveBasemap(basemap: any): void {
        if (!basemap) {
            return;
        }
        const {orthophotoTag, viewModel} = this.state;
        if (!viewModel) {
            return;
        }
        viewModel.activeBasemap = basemap;
        this.setState({
            activeBasemapId: basemap.portalItem.id,
            isOrthophoto: basemap.portalItem.tags.includes(orthophotoTag),
            viewModel
        });
    }

    /**
     * Set opacity to layers of base map
     *
     * @param layers
     */
    setLayerOpacity(layers: __esri.Collection<__esri.Layer>): void {
        const {transparency} = this.state;
        layers.forEach((layer: __esri.Layer) => {
            // Layer welche im Namen "Maske" enthalten ignorieren
            if(layer.title.toLowerCase().indexOf("maske")===-1) {
                layer.opacity = (100 - transparency) / 100;
            }
        });
    }
    setTransparency(transparency: number){
        const {store} = this.props;
        store?.mapStore.setBasemapOpacity(100 - transparency);
        this.setState(() => ({ transparency }));
    }

    componentDidMount(): void {
        const {config, onLoad, store} = this.props;
        // rerun until map view is ready and search view model is loaded
        autorun((reaction: any) => {
            if (store?.uiStore.widgets.has(this.widgetKey)) {
                reaction.dispose(); // stop autorun
                return;
            }

            const basemapGallery = this.loadBasemapGallery(config, store?.mapStore.view);
            if (!basemapGallery) {
                return;
            }
            if (store?.mapStore.basemapOpacity) {
                this.setState({
                    transparency: (100 - store?.mapStore.basemapOpacity)
                });
            }
            // filter the Basemaps
            const applicationBasemaps = config.applicationBasemaps;
            if(applicationBasemaps==="none"){
                //basemapGallery.destroy();
                store?.mapStore.removeBasemap();
                this.setState({
                    isLoading: false,
                    visible: false
                });
                reaction.dispose(); // stop autorun
                return;
            }
            const excludeApplicationBasemaps = config.excludeApplicationBasemaps;
            const excludeTags = config.excludeTags;
            if ("filterFunction" in basemapGallery.source) {
                if (applicationBasemaps && applicationBasemaps.length) {
                    if (excludeTags && excludeTags.length) {
                        basemapGallery.source.filterFunction = (basemap: any) =>
                            applicationBasemaps.includes(FormatUtils.formatTitle(basemap.portalItem.title))
                            && basemap.portalItem.tags.findIndex((tag: string) => excludeTags.includes(tag)) === -1;
                    }else{
                        basemapGallery.source.filterFunction = (basemap: any) =>
                            applicationBasemaps.includes(FormatUtils.formatTitle(basemap.portalItem.title));
                    }
                } else {
                    if (excludeApplicationBasemaps && excludeApplicationBasemaps.length
                        && excludeTags && excludeTags.length) {
                        basemapGallery.source.filterFunction = (basemap: any) =>
                            !excludeApplicationBasemaps.includes(FormatUtils.formatTitle(basemap.portalItem.title))
                            && basemap.portalItem.tags.findIndex((tag: string) => excludeTags.includes(tag)) === -1;
                    } else if (excludeApplicationBasemaps && excludeApplicationBasemaps.length) {
                        basemapGallery.source.filterFunction = (basemap: any) =>
                            !excludeApplicationBasemaps.includes(FormatUtils.formatTitle(basemap.portalItem.title));
                    } else if (excludeTags && excludeTags.length) {
                        basemapGallery.source.filterFunction = (basemap: any) =>
                            basemap.portalItem.tags.findIndex((tag: string) => excludeTags.includes(tag)) === -1;
                    }
                }
            }
            this.watchHandles.push(
                basemapGallery.watch("viewModel.items.length",
                    (newValue: any, oldValue: any, propertyName: string, target: any) => {
                        const viewModel = target.viewModel;
                        if (viewModel) {

                            const items = target.viewModel.items.items;
                            const portalItems = items.map((item: any) => {
                                item.basemap.watch("baseLayers.length",
                                    (newLength: any, oldLength: any, propName: any, basemap: any) => {
                                        if (newLength > 0) {
                                            runInAction(()=> {
                                                const titleFormated = FormatUtils.formatTitle(basemap.title);
                                                if (store?.mapStore.defaultBasemapTitle
                                                    === titleFormated){
                                                    store?.mapStore.setDefaultBasemap({
                                                        id: basemap.portalItem.id,
                                                        title: basemap.portalItem.title,
                                                        tags: basemap.portalItem.tags
                                                    });
                                                }
                                                if (this.state.activeBasemapId === null) {
                                                    if (store?.mapStore.defaultBasemapTitle
                                                        === titleFormated) {
                                                        this.setState((state: any) => ({
                                                            activeBasemapId: basemap.portalItem.id,
                                                            isOrthophoto: basemap.portalItem.tags.includes(
                                                                state.orthophotoTag)
                                                        }));
                                                        this.mapToggle[(this.state.isOrthophoto ? 1 : 0)] =
                                                            this.appendProxyUrl(basemap.portalItem.thumbnailUrl);
                                                    }
                                                } else if (this.state.isOrthophoto === null) {
                                                    if (this.state.activeBasemapId
                                                        === basemap.portalItem.id) {
                                                        this.setState((state: any) => ({
                                                            isOrthophoto: basemap.portalItem.tags.includes(
                                                                state.orthophotoTag)
                                                        }));
                                                        this.mapToggle[(this.state.isOrthophoto ? 1 : 0)] =
                                                            this.appendProxyUrl(basemap.portalItem.thumbnailUrl);
                                                    }
                                                }
                                            });
                                        }
                                    });
                                return item.basemap.portalItem;
                            });
                            const firstThumbnail = portalItems.find((element: any) =>
                                !element.tags.includes(this.state.orthophotoTag));
                            const secondThumbnail = portalItems.find((element: any) =>
                                element.tags.includes(this.state.orthophotoTag));
                            // Make sure that value is present
                            if (typeof firstThumbnail !== "undefined") {
                                this.mapToggle[0] = this.appendProxyUrl(firstThumbnail.thumbnailUrl);
                            } else {
                                this.mapToggle[0] = this.appendProxyUrl(secondThumbnail.thumbnailUrl);
                            }
                            if (typeof secondThumbnail !== "undefined") {
                                this.mapToggle[1] = this.appendProxyUrl(secondThumbnail.thumbnailUrl);
                            } else {
                                this.mapToggle[1] = this.mapToggle[0];
                            }

                            const dataAll = portalItems.map((portalItem: any) => new BasemapGalleryItemData({
                                id: portalItem.id,
                                title: portalItem.title,
                                tags: portalItem.tags,
                                isOrthophoto: portalItem.tags.includes(this.state.orthophotoTag),
                                thumbnailUrl: this.appendProxyUrl(portalItem.thumbnailUrl)
                            }));
                            // Hide if there are less than two basemaps
                            const visible = (dataAll.length > 1);

                            this.setState({
                                isLoading: false,
                                viewModel,
                                dataAll,
                                dataShown: dataAll,
                                activeBasemapId: basemapGallery.view.map.basemap.portalItem,
                                visible
                            });
                            store?.uiStore.addWidget(this.widgetKey, this);

                            if (onLoad) {
                                // call the hook
                                onLoad(viewModel);
                            }
                        }
                    })
            );

        });
        this.autorunDisposers.push(autorun(() => {
            if (!store) {
                return;
            }
            const {basemap} = store.mapStore;
            if (!basemap || !basemap.id) {
                return;
            }
            const selectedBasemap = this.getBasemap(basemap.id);
            if (selectedBasemap && selectedBasemap.portalItem.id !== this.state.activeBasemapId) {
                this.setState({
                    activeBasemapId: selectedBasemap.portalItem.id,
                    isOrthophoto: selectedBasemap.portalItem.tags.includes(this.state.orthophotoTag),
                });
            }
        }));
    }

    componentWillUnmount(): void {
        const {store} = this.props;
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        store?.uiStore.removeWidget(this.widgetKey);
    }

    loadBasemapGallery(galleryConfig: BasemapGalleryConfiguration,
                       view: __esri.MapView | __esri.SceneView | undefined): EsriBasemapGallery | null {
        if (!view) {
            return null;
        }

        const config = galleryConfig as __esri.BasemapGalleryProperties;
        const props = {
            view,
            ...config
        };
        return new EsriBasemapGallery(props);
    }

    appendProxyUrl(url: string): string {
        if (!this.props.proxyUrl) {
            return url;
        }

        return `${this.props.proxyUrl}?${url}`;
    }

}

export const BasemapGallery = withStore(BasemapGalleryBase);
